import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { readPublicShareToken } from '../quote-integration/customerShareUrl';
import {
  DEFAULT_UI_DENSITY,
  normalizeUiDensity,
  type UiDensityPreference,
} from '../services/ports/uiDensityPreference';
import { getSystemDensityVariables } from './uiDensity';

interface UiDensityContextValue {
  density: number;
  systemDensity: number;
  saved: boolean;
  saveError: boolean;
  setDensity(density: number): void;
  saveDensity(): void;
  resetDensity(): void;
}

const UiDensityContext = createContext<UiDensityContextValue>({
  density: DEFAULT_UI_DENSITY,
  systemDensity: DEFAULT_UI_DENSITY,
  saved: true,
  saveError: false,
  setDensity: () => {},
  saveDensity: () => {},
  resetDensity: () => {},
});

export function useUiDensity() {
  return useContext(UiDensityContext);
}

function subscribeToHashChange(onChange: () => void) {
  window.addEventListener('hashchange', onChange);
  return () => window.removeEventListener('hashchange', onChange);
}

export function UiDensityProvider({
  preference,
  children,
}: {
  preference: UiDensityPreference;
  children: ReactNode;
}) {
  const [density, updateDensity] = useState(() =>
    normalizeUiDensity(preference.read()),
  );
  const [savedDensity, setSavedDensity] = useState(density);
  const [saveError, setSaveError] = useState(false);
  const hash = useSyncExternalStore(
    subscribeToHashChange,
    () => window.location.hash,
    () => '',
  );
  // Public customer pages are not affected by an operator's local preference.
  const systemDensity = readPublicShareToken(hash) !== null ? 100 : density;
  useLayoutEffect(() => {
    const style = document.documentElement.style;
    const previous = Object.entries(
      getSystemDensityVariables(systemDensity),
    ).map(([key, value]) => {
      const original = style.getPropertyValue(key);
      style.setProperty(key, value);
      return [key, original] as const;
    });
    return () => {
      for (const [key, value] of previous) {
        if (value) style.setProperty(key, value);
        else style.removeProperty(key);
      }
    };
  }, [systemDensity]);
  const setDensity = (value: number) => {
    const next = normalizeUiDensity(value);
    updateDensity(next);
    setSaveError(false);
  };
  const saveDensity = () => {
    const success = preference.write(density);
    setSaveError(!success);
    if (success) setSavedDensity(density);
  };

  return (
    <UiDensityContext.Provider
      value={{
        density,
        systemDensity,
        saved: density === savedDensity && !saveError,
        saveError,
        setDensity,
        saveDensity,
        resetDensity: () => setDensity(DEFAULT_UI_DENSITY),
      }}
    >
      {children}
    </UiDensityContext.Provider>
  );
}
